/**
 * Signup screen — create a Beam account (business name + email + password) via
 * POST /api/public/signup, then route into the paywall gate to subscribe.
 *
 * In-app subscriptions (2026-06-19, design §5): "create account first, then subscribe".
 * The new account is created LOCKED (status=incomplete); after sign-in the AuthGate sends it
 * to /paywall to present the store product. This screen is NATIVE-focused — on web, account
 * creation routes to the marketing /pricing (Stripe) flow instead (see the "Create account"
 * link on login.tsx). It still renders safely on web.
 *
 * Built on the design system: Screen / Card / Field / Button / Text + BeamLogo. Loading + error
 * states rendered; no mock data — every submit is a real backend round-trip.
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSession } from '../src/auth/session';
import { ApiRequestError } from '../src/api/client';
import { Screen, Text, Field, Button } from '../src/components/ui';
import { BeamLogo } from '../src/components/brand/Logo';
import { theme } from '../src/theme';

export default function SignupScreen() {
  const signup = useSession((s) => s.signup);
  const router = useRouter();

  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSignup() {
    const business_name = businessName.trim();
    const trimmedEmail = email.trim();
    if (!business_name || !trimmedEmail || !password) {
      setError('Business name, email, and password are all required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await signup({ business_name, email: trimmedEmail, password });
      // Signed in immediately (locked). The AuthGate routes a locked native account to /paywall.
    } catch (err) {
      if (err instanceof ApiRequestError || err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Screen scroll>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container} testID="signup-screen">
          <View style={styles.brand}>
            <BeamLogo size={40} />
            <Text variant="subhead" color="secondary">
              Create your Beam account
            </Text>
          </View>

          <View style={styles.form}>
            <Field
              label="Business name"
              value={businessName}
              onChangeText={setBusinessName}
              autoCapitalize="words"
              placeholder="Acme Contracting"
              editable={!loading}
              accessibilityLabel="Business name"
              returnKeyType="next"
            />
            <Field
              label="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              textContentType="emailAddress"
              autoComplete="email"
              placeholder="you@example.com"
              editable={!loading}
              accessibilityLabel="Email address"
              returnKeyType="next"
            />
            <Field
              label="Password"
              value={password}
              onChangeText={setPassword}
              secureToggle
              textContentType="newPassword"
              autoComplete="password-new"
              placeholder="At least 8 characters"
              editable={!loading}
              accessibilityLabel="Password"
              returnKeyType="done"
              onSubmitEditing={handleSignup}
              error={error ?? undefined}
            />
            <Button
              title="Create account"
              onPress={handleSignup}
              loading={loading}
              disabled={loading}
              size="lg"
              style={styles.submit}
              testID="signup-submit"
              accessibilityLabel="Create account"
            />

            <View style={styles.altRow}>
              <Text variant="small" color="secondary">
                Already have an account?
              </Text>
              <Pressable accessibilityRole="link" onPress={() => router.replace('/login')}>
                <Text variant="small" color="link">
                  Sign in
                </Text>
              </Pressable>
            </View>

            <Text variant="small" color="secondary" style={styles.fine}>
              After creating your account you'll choose a subscription to unlock Beam. By continuing
              you agree to the Terms and Privacy Policy.
            </Text>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
  altRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.md },
  fine: { textAlign: 'center', marginTop: theme.spacing.sm },
});
